import { useState, useEffect, useCallback } from "react"
import Cookies from "js-cookie"
import { navigate } from "gatsby"
import { getSegmentDeviceId } from "@mindbodygreen/analytics-tracking-helpers"

import useTracking from "hooks/useTracking"
import { useCart } from "store/contexts/cart"

const useAddToCart = () => {
  const { track } = useTracking()
  const { getCart, shopifyCartToken, setShopifyCartToken } = useCart()
  const [addingToCart, setAddingToCart] = useState(null)
  const [addProdCart, setAddProdCart] = useState(null)
  const [addSubsCart, setAddSubsCart] = useState(null)

  const processCartData = useCallback(
    (data, callback, onLoadPage = false) => {
      try {
        const { token } = data
        setShopifyCartToken(token)
        Cookies.set("shopmbg_cart_token", token, {
          expires: 14,
          domain: process.env.GATSBY_SHOPIFY_COOKIE_DOMAIN,
        })
        if (!onLoadPage) {
          Cookies.set("cart", token, {
            expires: 14,
            domain: process.env.GATSBY_SHOPIFY_COOKIE_DOMAIN,
          })
        }
        callback(addProdCart, addSubsCart)
      } catch (err) {
        console.error(err.message)
      }
    },
    [setShopifyCartToken, addProdCart, addSubsCart],
  )

  const addClassToCart = useCallback(
    async (
      classId,
      clsTitle,
      clsPrice,
      redirectToCart = true,
      paymentType = "one-time",
      isBundle = false,
      termId = null,
    ) => {
      setAddingToCart(classId)

      track("Add To Cart Button Clicked", {
        product_id: classId,
        label: "product_id",
        category: "Class Checkout",
        paymentType,
      })

      try {
        const response = await fetch("/api/checkout/add-to-cart", {
          method: "POST",
          body: JSON.stringify({
            itemId: classId,
            ...(isBundle && { itemType: "bundle" }),
            ...(termId && { termId }),
            paymentType,
          }),
          headers: { "Content-Type": "application/json" },
        })

        if (!response.ok) {
          throw new Error(response.statusText)
        }

        const { id } = await response.json()

        Cookies.set("c_id", id, {
          expires: 30,
          domain: process.env.GATSBY_SHOPIFY_COOKIE_DOMAIN,
        })

        track("Product Added", {
          product_id: classId,
          cart_id: id,
          price: clsPrice,
          name: clsTitle,
          quantity: 1,
          currency: "USD",
          label: "product_id",
          brand: "mbg classes",
        })

        try {
          // fire addToCart for Google Tag Manager
          const d = window.gtmDataLayer || window.dataLayer || []
          d.push({ event: "addToCart", productId: classId })
        } catch (error) {
          // ignore
        }
        if (redirectToCart) {
          window.setTimeout(() => {
            navigate("/classes/checkout")
          }, 300)
        }
      } catch (err) {
        console.error(err)
      }

      getCart()
      setAddingToCart(null)
    },
    [getCart],
  )

  const addProductToCart = useCallback(
    async (
      productId,
      productPrice,
      quantity,
      productTitle,
      redirectToCart = true,
      redirectToCheckout = false,
      redirectToCartWithCheckoutParam = false,
      sellingPlanId = null,
    ) => {
      setAddingToCart(productId)

      track("Add To Cart Button Clicked", {
        product_id: productId,
        recurring: !!sellingPlanId,
        label: "product_id",
        category: "Product Checkout",
      })

      const itemProperties = sellingPlanId
        ? {
            _recurring: "true",
            _subscription: "true",
          }
        : {}

      const tokenCookie = Cookies.get("shopmbg_cart_token")
      const options = {
        items: [
          {
            quantity,
            id: productId,
            properties: itemProperties,
            ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
          },
        ],
        shopifyCartToken: tokenCookie,
      }

      const cartAttributes = {}
      try {
        // get Segment ID from Segment client library
        const segmentId = window.analytics?.user().anonymousId()
        cartAttributes.segmentId = segmentId
      } catch (err) {
        console.log(err)
      }

      try {
        cartAttributes.amplitudeDeviceId = getSegmentDeviceId()
        // per discussion with Gavin (friends@segment.com) this is where session ID is stored
        const amplitudeSessionId =
          window.amplitude?.getSessionId() || localStorage?.getItem("analytics_session_id")
        cartAttributes.amplitudeSessionId = amplitudeSessionId
      } catch (err) {
        console.log(err)
      }

      try {
        // get Google Analytics ID from ga library
        window.ga((tracker) => {
          cartAttributes.gaClientId = tracker.get("clientId")
        })
      } catch (err) {
        console.log(err)
      }

      options.attributes = cartAttributes
      try {
        const response = await fetch(`/api/shopify/add`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(options),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const data = await response.json()

        processCartData({ token: data.shopifyCartToken }, () => {})

        track("Product Added", {
          product_id: productId,
          cart_id: shopifyCartToken,
          price: productPrice,
          name: productTitle,
          quantity,
          recurring: !!sellingPlanId,
          currency: "USD",
          label: "product_id",
          brand: "mbg products",
        })

        if (redirectToCheckout) {
          window.setTimeout(() => {
            navigate(`${process.env.GATSBY_SHOPIFY_URL}/checkout`)
          }, 300)
        } else if (redirectToCartWithCheckoutParam) {
          // Redirects to the Shopify cart with intention of forwarding to checkout after
          window.setTimeout(() => {
            navigate(`${process.env.GATSBY_SHOPIFY_URL}/cart?checkout=true`)
          }, 300)
        } else if (redirectToCart) {
          window.setTimeout(() => {
            navigate(`${process.env.GATSBY_SHOPIFY_URL}/cart`)
          }, 300)
        }
      } catch (err) {
        console.error(err)
      }

      getCart()
      setAddingToCart(null)
    },
    [shopifyCartToken, getCart, processCartData],
  )

  const addToCart = useCallback(
    (propProd, propSubs) => {
      /**
       * Currently redirect value is being used in all cases and useAddToCart prioritizes
       * sending to checkout first. In the future may redesign data model for more
       * checkout / cart options
       */
      if (propProd) {
        const { redirect, productId, productPrice, quantity, productTitle } = propProd
        addProductToCart(
          productId,
          productPrice,
          quantity,
          productTitle,
          redirect,
          redirect,
          redirect,
        )
      }
      if (propSubs) {
        const { redirect, productId, productPrice, quantity, productTitle, sellingPlanId } =
          propSubs

        addProductToCart(
          productId,
          productPrice,
          quantity,
          productTitle,
          redirect,
          redirect,
          redirect,
          sellingPlanId,
        )
      }
    },

    [addProductToCart],
  )

  const getCartToken = useCallback(
    (addProd, addSubs) => {
      setAddProdCart(addProd)
      setAddSubsCart(addSubs)
      if (!document.getElementById("shopify-cart-json")) {
        const script = document.createElement("script")
        script.id = "shopify-cart-json"
        script.src = `${process.env.GATSBY_SHOPIFY_URL}/cart.json?callback=setCartData`
        document.getElementsByTagName("head")[0].appendChild(script)
      } else {
        addToCart(addProd, addSubs)
      }
    },
    [addToCart],
  )

  useEffect(() => {
    window.setCartData = (data) => {
      processCartData(data, addToCart)
    }
  }, [processCartData, addToCart])

  return { addingToCart, addClassToCart, addProductToCart, getCartToken, processCartData }
}

export default useAddToCart
